// 배송비는 상품별 설정이 있으면 그것을, 없으면 사이트 기본값을 따른다.
// 계산식이 주문 API와 주문 폼 양쪽에서 이 함수를 공유해야 결제 금액이 어긋나지 않는다.

export const DEFAULT_DELIVERY_FEE = 3000;
export const DEFAULT_FREE_DELIVERY_AT = 29800;

type ShippingSource =
  | { deliveryFee?: number | null; freeDeliveryAt?: number | null }
  | null
  | undefined;

export type ShippingRule = { deliveryFee: number; freeDeliveryAt: number };

export function resolveShipping(
  product: ShippingSource,
  settings: ShippingSource,
): ShippingRule {
  return {
    deliveryFee:
      product?.deliveryFee ?? settings?.deliveryFee ?? DEFAULT_DELIVERY_FEE,
    freeDeliveryAt:
      product?.freeDeliveryAt ??
      settings?.freeDeliveryAt ??
      DEFAULT_FREE_DELIVERY_AT,
  };
}

// freeDeliveryAt 0 = 0원 이상이므로 항상 무료배송.
export function calcDeliveryFee(subtotal: number, rule: ShippingRule): number {
  return subtotal >= rule.freeDeliveryAt ? 0 : rule.deliveryFee;
}

// 판매 중인 상품들의 무료배송 기준이 모두 같으면 그 금액, 하나라도 다르면 null.
export function commonFreeDeliveryAt(
  products: ShippingSource[],
  settings: ShippingSource,
): number | null {
  if (products.length === 0) {
    return resolveShipping(null, settings).freeDeliveryAt;
  }
  const values = new Set(
    products.map((p) => resolveShipping(p, settings).freeDeliveryAt),
  );
  return values.size === 1 ? [...values][0] : null;
}

// 판매 중인 상품들의 배송비가 모두 같으면 그 금액, 하나라도 다르면 null.
export function commonDeliveryFee(
  products: ShippingSource[],
  settings: ShippingSource,
): number | null {
  if (products.length === 0) {
    return resolveShipping(null, settings).deliveryFee;
  }
  const values = new Set(
    products.map((p) => resolveShipping(p, settings).deliveryFee),
  );
  return values.size === 1 ? [...values][0] : null;
}

// 장바구니(여러 상품)의 배송비 규칙. 상품마다 배송비/무료배송 기준이 다를
// 수 있으므로(관리자가 상품별로 설정 가능), 묶음배송 시 어느 상품 기준을
// 따를지 정해야 한다. "묶음배송 최소부가"(가장 유리한 조건을 적용) 관행에
// 따라 배송비는 담긴 상품 중 가장 낮은 값을, 무료배송 기준도 가장 낮은
// 값을 각각 적용한다(두 값이 서로 다른 상품에서 올 수 있다 — 예: A 상품은
// 배송비 2,000원이 가장 싸고, B 상품은 무료배송 기준 10,000원이 가장
// 낮으면 "2,000원, 10,000원 이상 무료"로 합쳐 적용). 손님에게 불리하지
// 않은 쪽으로만 정해지므로 상품별 설정을 그대로 두고도 장바구니 배송비를
// 안전하게 계산할 수 있다.
export function resolveCartShipping(
  products: ShippingSource[],
  settings: ShippingSource,
): ShippingRule {
  if (products.length === 0) {
    return resolveShipping(null, settings);
  }
  const rules = products.map((product) => resolveShipping(product, settings));
  return {
    deliveryFee: Math.min(...rules.map((rule) => rule.deliveryFee)),
    freeDeliveryAt: Math.min(...rules.map((rule) => rule.freeDeliveryAt)),
  };
}

// 무료배송까지 남은 금액. 이미 무료배송 조건을 채웠거나 무료배송 기준
// 자체가 없으면(0원, 즉 항상 무료) null.
export function amountUntilFreeShipping(
  subtotal: number,
  freeDeliveryAt: number,
): number | null {
  if (freeDeliveryAt <= 0) return null;
  const remaining = freeDeliveryAt - subtotal;
  return remaining > 0 ? remaining : null;
}

export type ShippingDisplay = {
  deliveryFee: number | null;
  freeDeliveryAt: number | null;
};

// 배송비/무료배송 기준 문구에 쓸 값. 판매 중인 상품 전체가 같은 값을 쓰면 그
// 값을, 상품마다 달라 하나로 말할 수 없으면 null을 돌려준다. 실제 문구
// 조립은 화면마다 다르므로(약관·고객센터) 이 함수는 값만 계산한다.
export function resolveShippingDisplay(
  products: ShippingSource[],
  settings: ShippingSource,
): ShippingDisplay {
  return {
    deliveryFee: commonDeliveryFee(products, settings),
    freeDeliveryAt: commonFreeDeliveryAt(products, settings),
  };
}
